import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Search, ChevronDown, X, Check } from 'lucide-react';
import { SAMPLE_PRODUCTS } from './PillarTemplate';
import { getVendorMetadata } from '../../../../lib/vendorCoverage';
import './ArchitectureVendorPicker.css';

/**
 * Extract unique vendors from SAMPLE_PRODUCTS across all pillars
 */
function extractVendorsFromProducts() {
  const vendorSet = new Set();
  Object.values(SAMPLE_PRODUCTS).forEach(products => {
    products.forEach(p => {
      if (p.name && p.name !== 'Various') {
        vendorSet.add(p.name);
      }
    });
  });

  // Convert to array with metadata, sorted alphabetically
  return Array.from(vendorSet)
    .map(name => ({
      name,
      ...getVendorMetadata(name)
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * ArchitectureVendorPicker - Multi-select vendor picker for highlighting nodes
 * in the Architecture Diagram by vendor.
 *
 * Props:
 *   selectedVendors - Array of vendor name strings
 *   onToggle - (vendorName: string) => void
 *   onClear - () => void
 */
export default function ArchitectureVendorPicker({ selectedVendors = [], onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Get all available vendors from SAMPLE_PRODUCTS
  const allVendors = useMemo(() => extractVendorsFromProducts(), []);

  // Filter by search query
  const filteredVendors = useMemo(() => {
    if (!query.trim()) return allVendors;
    const q = query.toLowerCase();
    return allVendors.filter(v =>
      v.name.toLowerCase().includes(q) ||
      (v.aliases || []).some(a => a.toLowerCase().includes(q))
    );
  }, [allVendors, query]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Focus search when dropdown opens
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    } else if (!open) {
      setQuery('');
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleClear = (e) => {
    e.stopPropagation();
    onClear?.();
  };

  const hasSelection = selectedVendors.length > 0;

  // Get metadata for selected vendors
  const selectedWithMeta = useMemo(() =>
    selectedVendors.map(name => ({ name, ...getVendorMetadata(name) })),
    [selectedVendors]
  );

  return (
    <div className="avp-container" ref={containerRef}>
      <button
        type="button"
        className={`avp-trigger ${hasSelection ? 'avp-trigger--has-selection' : ''}`}
        onClick={() => setOpen(o => !o)}
        title={
          hasSelection
            ? `Highlighting ${selectedVendors.length} vendor(s). Click to edit.`
            : 'Highlight nodes by vendor'
        }
      >
        {hasSelection ? (
          <>
            <span className="avp-trigger-dots">
              {selectedWithMeta.slice(0, 4).map(v => (
                <span
                  key={v.name}
                  className="avp-trigger-dot"
                  style={{ background: v.displayColor }}
                  title={v.name}
                />
              ))}
              {selectedVendors.length > 4 && (
                <span className="avp-trigger-dot-more">+{selectedVendors.length - 4}</span>
              )}
            </span>
            <span className="avp-trigger-label">
              {selectedVendors.length === 1
                ? selectedVendors[0]
                : `${selectedVendors.length} vendors`}
            </span>
            <span
              className="avp-trigger-clear"
              onClick={handleClear}
              role="button"
              aria-label="Clear all vendor highlights"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClear(e); }}
            >
              <X size={12} />
            </span>
          </>
        ) : (
          <>
            <Search size={14} />
            <span>Highlight vendor</span>
            <ChevronDown size={12} className={open ? 'avp-chevron-rotated' : ''} />
          </>
        )}
      </button>

      {open && (
        <div className="avp-dropdown" role="listbox" aria-multiselectable="true">
          <div className="avp-search">
            <Search size={14} className="avp-search-icon" />
            <input
              ref={inputRef}
              type="search"
              className="avp-search-input"
              placeholder="Search vendors..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {filteredVendors.length === 0 ? (
            <div className="avp-status avp-status--empty">
              {query ? `No vendors match "${query}"` : 'No vendors available'}
            </div>
          ) : (
            <>
              <ul className="avp-list">
                {filteredVendors.map(vendor => {
                  const isSelected = selectedVendors.includes(vendor.name);
                  return (
                    <li key={vendor.name} className="avp-row">
                      <button
                        type="button"
                        className={`avp-option ${isSelected ? 'avp-option--selected' : ''}`}
                        onClick={() => onToggle?.(vendor.name)}
                        role="option"
                        aria-selected={isSelected}
                      >
                        <span className="avp-option-check">
                          {isSelected && <Check size={12} />}
                        </span>
                        <span
                          className="avp-option-dot"
                          style={{ background: vendor.displayColor }}
                        />
                        {vendor.logoUrl && (
                          <img
                            src={vendor.logoUrl}
                            alt=""
                            className="avp-option-logo"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span className="avp-option-name">{vendor.name}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {hasSelection && (
                <div className="avp-footer">
                  <button type="button" className="avp-clear-all" onClick={onClear}>
                    Clear all ({selectedVendors.length})
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
